import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  Briefcase,
  ChartLineUp,
  Lightning,
  MagnifyingGlass,
  Download,
  Link,
  PencilLine,
  TreeStructure,
  X,
} from "@phosphor-icons/react";
import { TOKEN_COSTS } from "@stylingcv/config";
import { getStreamingGenerationCost } from "@/client/services/token/token-config.service";
import { cn } from "@stylingcv/utils";
import { motion, AnimatePresence } from "framer-motion";
import React, { useState, useEffect, useCallback, useRef } from "react";

import { useTouchOptimization } from "@stylingcv/hooks";

import { ATSStrengthProvider } from "@/client/contexts/ATSStrengthContext";
import { useFormFocus } from "@/client/contexts/form-focus-context";
import { useTokenModal } from "@/client/contexts/token-modal-context";
import { useGenerationStream } from "@/client/hooks/use-generation-stream";
import { ResumeForm } from "@/client/pages/smart-generator/components/ResumeForm";
import { TokenProvider, useTokens } from "@/client/pages/smart-generator/contexts/TokenContext";
import { useResumeTransfer } from "@/client/pages/smart-generator/hooks/useResumeTransfer";
import { centralizedTokenService } from "@/client/services/token/centralized-token.service";
import { useAuthStore } from "@/client/stores/auth";
import { useDynamicCountryDetection } from "@/client/hooks/use-dynamic-country-detection";
import {
  trackGenerateResumeStart,
  trackGenerateResumeComplete,
  trackGenerateResumeFailed,
} from "@/client/utils/gtm";
import {
  isInsufficientTokensMessage,
  parseRequiredTokensFromMessage,
} from "@/client/utils/parse-ai-gateway-error";

import { GenerationOverlay } from "./generation-overlay";
import { UploadResumeModal } from "./upload-modal";

type FormSubmitData = {
  jobRole: string;
  language: string;
  country: string;
  experience: string;
  tone?: string;
  additionalDescription?: string;
  jobUrl?: string;
};

// Premium 3D ATS Shield Component
const AtsShieldIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg viewBox="0 0 220 260" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <defs>
      <filter id="shield-shadow" x="-50%" y="-50%" width="200%" height="200%">
        <feDropShadow dx="0" dy="10" stdDeviation="8" floodColor="#000" floodOpacity="0.5" />
      </filter>
      <linearGradient id="shield-border" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#F1F5F9" />
        <stop offset="50%" stopColor="#94A3B8" />
        <stop offset="100%" stopColor="#475569" />
      </linearGradient>
      <linearGradient id="shield-body" x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="100%" stopColor="#f8fafc" />
      </linearGradient>
      <linearGradient id="gold-ribbon" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#B45309" />
        <stop offset="20%" stopColor="#F59E0B" />
        <stop offset="50%" stopColor="#FCD34D" />
        <stop offset="80%" stopColor="#F59E0B" />
        <stop offset="100%" stopColor="#B45309" />
      </linearGradient>
    </defs>
    <g filter="url(#shield-shadow)">
      <path
        d="M110 240C110 240 190 200 190 110V40L110 20L30 40V110C30 200 110 240 110 240Z"
        fill="url(#shield-border)"
      />
      <path
        d="M110 232C110 232 182 196 182 110V46L110 28L38 46V110C38 196 110 232 110 232Z"
        fill="url(#shield-body)"
      />
    </g>
    <path d="M110 28L182 46V110C182 140 170 170 150 190L110 28Z" fill="black" fillOpacity="0.03" />
    <circle cx="110" cy="85" r="35" fill="#10B981" fillOpacity="0.15" />
    <circle cx="110" cy="85" r="28" fill="#10B981" />
    <path
      d="M96 85L106 95L124 75"
      stroke="white"
      strokeWidth="5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <text
      x="110"
      y="150"
      textAnchor="middle"
      fontFamily="Arial, sans-serif"
      fontWeight="900"
      fontSize="36"
      fill="#334155"
      letterSpacing="2"
    >
      ATS
    </text>
    <text
      x="110"
      y="170"
      textAnchor="middle"
      fontFamily="Arial, sans-serif"
      fontWeight="500"
      fontSize="11"
      fill="#94A3B8"
      letterSpacing="3"
      style={{ textTransform: "uppercase" }}
    >
      {t`FRIENDLY`}
    </text>
    <g transform="translate(110, 200)">
      <path d="M-55 -5L-65 -15V15L-55 5V-5Z" fill="#78350F" />
      <path d="M55 -5L65 -15V15L55 5V-5Z" fill="#78350F" />
      <path d="M-60 -8H60L55 12H-55L-60 -8Z" fill="url(#gold-ribbon)" />
      <text
        x="0"
        y="6"
        textAnchor="middle"
        fontFamily="Arial, sans-serif"
        fontWeight="bold"
        fontSize="13"
        fill="#3D1500"
      >
        {t`100% COMPATIBLE`}
      </text>
    </g>
  </svg>
);

// MCP Agent data - using function to access translations
const getMcpAgents = () => [
  {
    icon: MagnifyingGlass,
    name: t`Market Scout`,
    desc: t`Job Intelligence`,
    position: "left-[8%] top-[25%]",
    delay: 0.5,
    color: "from-blue-400 to-cyan-400",
  },
  {
    icon: TreeStructure,
    name: t`Architect`,
    desc: t`AI Structure`,
    position: "left-[6%] bottom-[25%]",
    delay: 0.7,
    color: "from-purple-400 to-pink-400",
  },
  {
    icon: ChartLineUp,
    name: t`ATS Engine`,
    desc: t`98% Pass Rate`,
    customComponent: (
      <div className="flex flex-col items-center">
        <AtsShieldIcon className="h-24 w-auto drop-shadow-xl transition-transform duration-500 hover:scale-110" />
        <div className="mt-2 text-center">
          <div className="text-sm font-bold text-[#3e3431]">{t`ATS Engine`}</div>
          <div className="text-xs font-medium uppercase tracking-widest text-[#3e3431]/70">{t`98% Pass Rate`}</div>
        </div>
      </div>
    ),
    position: "right-[8%] top-[35%]",
    delay: 0.9,
    color: "from-amber-400 to-orange-400",
  },
];

// Animated floating badge component
const AgentBadge = ({
  icon: Icon,
  name,
  desc,
  position,
  delay,
  color,
  customComponent,
}: ReturnType<typeof getMcpAgents>[0] & { customComponent?: React.ReactNode }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.8, y: 20 }}
    animate={{ opacity: 1, scale: 1, y: 0 }}
    transition={{ delay, duration: 0.6, ease: "easeOut" }}
    className={cn("absolute hidden items-center gap-3 lg:flex", position)}
  >
    <motion.div
      animate={{ y: [0, -8, 0] }}
      transition={{
        duration: 3 + Math.random() * 2,
        repeat: Infinity,
        ease: "easeInOut",
        delay: delay * 0.5,
      }}
      className="flex items-center gap-3"
    >
      {customComponent ? (
        customComponent
      ) : (
        <>
          <div className="relative">
            <div
              className={cn(
                "absolute inset-0 rounded-full bg-gradient-to-r opacity-50 blur-lg",
                color,
              )}
            />
            <div
              className={cn(
                "relative flex size-11 items-center justify-center rounded-full bg-gradient-to-r shadow-lg",
                color,
              )}
            >
              <Icon weight="bold" className="size-5 text-white" />
            </div>
          </div>
          <div>
            <div className="text-sm font-semibold text-[#3e3431]">{name}</div>
            <div className="text-xs text-[#3e3431]/70">{desc}</div>
          </div>
          <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
            className={cn("size-2 rounded-full bg-gradient-to-r", color)}
          />
        </>
      )}
    </motion.div>
  </motion.div>
);

// New Mobile Agent Showcase
const MobileAgentIcons = () => {
  const agents = getMcpAgents();
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.55, duration: 0.5 }}
      className="mb-8 flex w-full max-w-sm flex-row items-start justify-center gap-3 px-2 sm:gap-6 lg:hidden"
    >
      {agents.map((agent) => (
        <div
          key={agent.name}
          className="flex flex-1 flex-col items-center justify-start gap-2 opacity-80 grayscale transition-all hover:opacity-100 hover:grayscale-0"
        >
          <div className="mx-auto flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-white shadow-md ring-1 ring-black/5">
            <div className="flex h-full w-full items-center justify-center rounded-[11px] bg-slate-50">
              {agent.icon ? (
                <agent.icon
                  weight="bold"
                  className={cn("h-5 w-5", agent.color.replace("from-", "text-").split(" ")[0])}
                />
              ) : (
                <div className="scale-[0.5]">
                  <AtsShieldIcon className="h-12 w-auto" />
                </div>
              )}
            </div>
          </div>
          <div className="w-full text-center text-[10px] font-bold uppercase leading-tight tracking-tighter text-[#3e3431]/70">
            {agent.name}
          </div>
        </div>
      ))}
    </motion.div>
  );
};

const AtmosphericHeroContent = () => {
  const STREAMING_COST = getStreamingGenerationCost();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [showGenerationOverlay, setShowGenerationOverlay] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [lastFormData, setLastFormData] = useState<{
    jobRole: string;
    country: string;
    jobUrl?: string;
  }>({ jobRole: "", country: "" });
  const { isFormFocused, setIsFormFocused } = useFormFocus();

  const countryData = useDynamicCountryDetection();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [quickStartData, setQuickStartData] = useState<{
    jobRole?: string;
    additionalDescription?: string;
    jobUrl?: string;
  } | null>(null);

  const mcpAgents = getMcpAgents();
  const { tokenBalance, refreshTokenBalance, canAfford, decrementTokens } = useTokens();
  const { showTokenModal } = useTokenModal();
  const isLoggedIn = useAuthStore((state) => !!state.user);
  const { triggerHaptic } = useTouchOptimization();
  const { i18n } = useLingui();
  const isRTL = i18n.locale === "ar-SA";
  const heroFlowWords = isRTL
    ? ["يحلل", "يفكر", "يكتب", "ينسق", "يصمم"]
    : [t`analyzes`, t`thinks`, t`writes`, t`formats`, t`designs`];
  const [activeFlowIndex, setActiveFlowIndex] = useState(0);
  const flowLength = heroFlowWords.length;

  const streamingHook = useGenerationStream();
  const { transferToBuilder } = useResumeTransfer();

  const generationAnalyticsRef = useRef<{
    startTime: number;
    jobTitle: string;
    experienceLevel: string;
    language: string;
    country: string;
    tone: string;
    completed: boolean;
  } | null>(null);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      void refreshTokenBalance();
    }, 100);
    return () => clearTimeout(timer);
  }, [refreshTokenBalance]);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFlowIndex((prev) => (prev + 1) % flowLength);
    }, 950);

    return () => clearInterval(interval);
  }, [flowLength]);

  useEffect(() => {
    const handleTransfer = async () => {
      if (streamingHook.resumeData && !streamingHook.isGenerating) {
        if (generationAnalyticsRef.current && !generationAnalyticsRef.current.completed) {
          const elapsed = Math.round(
            (Date.now() - generationAnalyticsRef.current.startTime) / 1000,
          );
          trackGenerateResumeComplete({
            jobTitle: generationAnalyticsRef.current.jobTitle,
            generationTime: elapsed,
          });
          generationAnalyticsRef.current.completed = true;
        }

        try {
          const result = await transferToBuilder(streamingHook.resumeData);
          if (!result || (result && typeof result === "object" && "error" in result)) {
            setGenerationError(t`Failed to process resume data. Please try again.`);
            return;
          }
          setShowGenerationOverlay(false);
          streamingHook.reset();
          triggerHaptic("success");
        } catch (error: any) {
          setGenerationError(error?.message || "Failed to transfer resume");
        }
      }
    };
    handleTransfer();
  }, [streamingHook.resumeData, streamingHook.isGenerating, transferToBuilder]);

  useEffect(() => {
    if (!streamingHook.error) return;

    const isTokenError =
      streamingHook.errorCode === "INSUFFICIENT_TOKENS" ||
      streamingHook.errorCode === "SESSION_LIMIT_EXCEEDED" ||
      isInsufficientTokensMessage(streamingHook.error);

    if (isTokenError) {
      setShowGenerationOverlay(false);
      setGenerationError(null);
      const required = parseRequiredTokensFromMessage(streamingHook.error) ?? STREAMING_COST;
      showTokenModal({
        requiredTokens: required,
        currentTokens: tokenBalance,
        featureName: t`AI Resume Generation`,
      });
    } else {
      setGenerationError(streamingHook.error);
    }

    if (generationAnalyticsRef.current && !generationAnalyticsRef.current.completed) {
      trackGenerateResumeFailed({
        jobTitle: generationAnalyticsRef.current.jobTitle,
        errorMessage: streamingHook.error,
      });
      generationAnalyticsRef.current = null;
    }
  }, [streamingHook.error, streamingHook.errorCode, showTokenModal, tokenBalance, STREAMING_COST]);

  useEffect(() => {
    if (
      !streamingHook.isGenerating &&
      showGenerationOverlay &&
      !streamingHook.resumeData &&
      !streamingHook.error &&
      !generationError
    ) {
      if (streamingHook.sessionId) {
        console.warn("[Generation] Silent failure detected - no data, no error");
        setGenerationError(t`Generation failed unexpectedly. Please try again.`);
        if (generationAnalyticsRef.current && !generationAnalyticsRef.current.completed) {
          trackGenerateResumeFailed({
            jobTitle: generationAnalyticsRef.current.jobTitle,
            errorMessage: "Silent failure — generation stopped without data or error",
          });
          generationAnalyticsRef.current = null;
        }
      }
    }
  }, [
    streamingHook.isGenerating,
    streamingHook.resumeData,
    streamingHook.error,
    streamingHook.sessionId,
    showGenerationOverlay,
    generationError,
  ]);

  const startInlineGeneration = useCallback(
    async (params: {
      role: string;
      country: string;
      language: string;
      experience: string;
      tone: string;
      description?: string;
      jobUrl?: string;
    }) => {
      try {
        setGenerationError(null);

        const freshBalance = await centralizedTokenService.getTokenBalance(isLoggedIn, true);
        if (freshBalance.balance < STREAMING_COST) {
          await refreshTokenBalance();
          showTokenModal({
            requiredTokens: STREAMING_COST,
            currentTokens: freshBalance.balance,
            featureName: t`AI Resume Generation`,
          });
          return;
        }

        setShowGenerationOverlay(true);

        generationAnalyticsRef.current = {
          startTime: Date.now(),
          jobTitle: params.role,
          experienceLevel: params.experience,
          language: params.language,
          country: params.country,
          tone: params.tone,
          completed: false,
        };
        trackGenerateResumeStart({
          jobTitle: params.role,
          experienceLevel: params.experience,
          language: params.language,
          country: params.country,
          tone: params.tone,
        });

        triggerHaptic("medium");
        await streamingHook.startGeneration({
          role: params.role,
          location: params.country,
          language: params.language,
          experience: params.experience,
          tone: params.tone,
        });
      } catch (error: any) {
        setGenerationError(error?.message || t`Failed to start generation`);
        if (generationAnalyticsRef.current && !generationAnalyticsRef.current.completed) {
          trackGenerateResumeFailed({
            jobTitle: generationAnalyticsRef.current.jobTitle,
            errorMessage: error?.message || "Failed to start generation",
          });
          generationAnalyticsRef.current = null;
        }
      }
    },
    [isLoggedIn, refreshTokenBalance, showTokenModal, streamingHook, STREAMING_COST, triggerHaptic],
  );

  const handleSubmit = (data: FormSubmitData) => {
    setLastFormData({ jobRole: data.jobRole, country: data.country, jobUrl: data.jobUrl });
    setIsFormFocused(false);
    startInlineGeneration({
      role: data.jobRole,
      country: data.country,
      language: data.language,
      experience: data.experience,
      tone: data.tone || "Professional",
      description: data.additionalDescription,
      jobUrl: data.jobUrl,
    });
  };

  const handleOverlayClose = () => {
    setShowGenerationOverlay(false);
    setGenerationError(null);
    streamingHook.reset();
  };

  const handleFormClose = () => {
    setIsFormFocused(false);
    document.body.style.overflow = "unset";
  };

  React.useEffect(() => {
    const handleTriggerAIForm = () => setIsFormFocused(true);
    window.addEventListener("triggerAIForm", handleTriggerAIForm);
    return () => window.removeEventListener("triggerAIForm", handleTriggerAIForm);
  }, []);

  React.useEffect(() => {
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  if (isFormFocused) {
    return (
      <div className="fixed inset-0 z-[99999] bg-[#fdfcfb]/95 backdrop-blur-xl">
        <style>{`body { overflow: hidden; }`}</style>
        <div className="h-full overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4 py-8">
            <div className="relative w-full max-w-2xl">
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="fixed right-4 top-4 z-[100000] rounded-full border border-[#3e3431]/10 bg-white p-3 shadow-md transition-all duration-200 hover:scale-110 hover:bg-gray-50"
                onClick={handleFormClose}
              >
                <X className="size-6 text-[#3e3431]" />
              </motion.button>
              <motion.div
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="overflow-hidden rounded-2xl border border-[#3e3431]/10 bg-white shadow-2xl"
              >
                <div className="border-b border-[#3e3431]/5 p-6 pb-4 text-center">
                  <h2 className="mb-2 font-serif text-2xl font-bold text-[#3e3431]">{t`Create Perfect Resume`}</h2>
                  <p className="text-[#3e3431]/60">{t`Fill in your details below`}</p>
                </div>
                <div className="p-0">
                  <ResumeForm
                    isLoading={isRedirecting}
                    tokenBalance={tokenBalance}
                    onSubmit={handleSubmit}
                    onRefreshTokens={refreshTokenBalance}
                    initialFormData={
                      lastFormData.jobRole
                        ? {
                            jobRole: lastFormData.jobRole,
                            country: lastFormData.country,
                            jobUrl: lastFormData.jobUrl,
                          }
                        : undefined
                    }
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      dir={isRTL ? "rtl" : undefined}
      className={cn("relative min-h-screen overflow-hidden", isRTL && "rtl")}
    >
      <GenerationOverlay
        isOpen={showGenerationOverlay}
        onClose={handleOverlayClose}
        formData={lastFormData}
        progress={streamingHook.overallProgress}
        currentPhase={streamingHook.currentPhase}
        currentMessage={streamingHook.currentMessage}
        isThinking={streamingHook.isThinking}
        error={generationError}
        errorCode={streamingHook.errorCode}
        insights={streamingHook.insights}
        phases={streamingHook.phases}
        pipelineStartTime={streamingHook.pipelineStartTime}
      />

      <UploadResumeModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        language={i18n.locale?.startsWith("ar") ? "AR" : "EN"}
      />

      {/* Hero background — Soft Editorial */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden bg-[#FDFBF7]">
        {/* Soft, warm vignette */}
        <div
          className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(255,255,255,1)_0%,_transparent_60%)]"
          aria-hidden
        />
        <div
          className="absolute inset-0 bg-gradient-to-b from-[#FDFBF7]/40 via-[#FDFBF7]/80 to-[#FDFBF7]"
          aria-hidden
        />
        {/* Very subtle color mesh - Soft peach/sage */}
        <div className="absolute inset-0 opacity-[0.35]">
          <div
            className="absolute -left-[10%] -top-[20%] h-[80%] w-[70%] rounded-full blur-[120px]"
            style={{
              background:
                "radial-gradient(ellipse at center, rgba(217, 119, 87, 0.1) 0%, transparent 60%)",
            }}
          />
          <div
            className="absolute right-[0%] top-[10%] h-[60%] w-[50%] rounded-full blur-[100px]"
            style={{
              background:
                "radial-gradient(ellipse at center, rgba(20, 184, 138, 0.08) 0%, transparent 60%)",
            }}
          />
        </div>
        {/* Subtle noise for editorial feel */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />
      </div>

      {/* Main Content */}
      <div className="relative z-10 -mt-4 flex min-h-[100dvh] flex-col items-center justify-center px-4 py-24 sm:-mt-4 sm:px-6 sm:py-32">
        {/* Eyebrow + headline */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.7, ease: [0.32, 0.72, 0, 1] }}
          className="relative mb-8 max-w-[920px] text-center"
        >
          <div className="mb-6 flex justify-center">
            <span className="rounded-full bg-white border border-[#3e3431]/10 px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#D97757] shadow-sm">
              {t`Free AI Resume Builder`}
            </span>
          </div>
          <h1 className="font-serif text-[clamp(2.5rem,7vw,5rem)] font-extrabold leading-[1.05] tracking-tight text-[#3e3431]">
            {t`ATS-optimized.`}
            <br />
            <span className="bg-gradient-to-r from-[#14b88a] to-[#2C7A7B] bg-clip-text text-transparent">
              {t`Recruiter-ready.`}
            </span>
          </h1>
        </motion.div>

        {/* Subtitle + animated capability flow */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, duration: 0.6, ease: [0.32, 0.72, 0, 1] }}
          className="relative mb-12 max-w-3xl px-2 text-center"
        >
          <p className="text-[clamp(1.1rem,2.5vw,1.35rem)] leading-relaxed text-[#475569] font-medium">
            {t`Stylion's AI writes, scores, and tailors your resume to each job description — so it passes automated screening and reads well to a human.`}
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-2 sm:gap-3">
            {heroFlowWords.map((word, index) => (
              <React.Fragment key={word}>
                <motion.span
                  animate={{
                    opacity: activeFlowIndex === index ? 1 : 0.4,
                    y: activeFlowIndex === index ? -2 : 0,
                    scale: activeFlowIndex === index ? 1.05 : 1,
                  }}
                  transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
                  className="px-1 text-[clamp(1rem,2.2vw,1.25rem)] font-bold tracking-tight"
                >
                  <span className="text-[#3e3431]">
                    {word}
                  </span>
                </motion.span>
                {index < heroFlowWords.length - 1 && (
                  <motion.span
                    aria-hidden
                    animate={{ opacity: activeFlowIndex === index ? 0.7 : 0.2 }}
                    className="inline-flex items-center text-[#3e3431]/20"
                  >
                    {isRTL ? (
                      <ArrowLeft weight="bold" className="size-3.5 sm:size-4" />
                    ) : (
                      <ArrowRight weight="bold" className="size-3.5 sm:size-4" />
                    )}
                  </motion.span>
                )}
              </React.Fragment>
            ))}
          </div>
        </motion.div>

        {/* Form - shared ResumeForm */}
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.6, duration: 0.8, ease: [0.32, 0.72, 0, 1] }}
          className="relative w-full max-w-4xl"
        >
          <div className="mb-6 flex flex-col items-center justify-center gap-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#3e3431]/10 bg-white px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-[#3e3431] shadow-sm">
              <Briefcase weight="duotone" className="size-4 text-[#14b88a]" />
              <span>{t`Powered by 11 Specialized AI Agents`}</span>
            </div>
            <div className="inline-flex items-center gap-2 rounded-full border border-transparent bg-[#3e3431]/5 px-4 py-1.5 text-[11px] font-semibold tracking-wide text-[#3e3431]/60">
              <span>{t`Build your resume in ~2 minutes — start free`}</span>
              <ArrowDown weight="bold" className="size-3.5 text-[#D97757]" />
            </div>
          </div>
          
          <div className="relative rounded-[2rem] border border-[#3e3431]/10 bg-white p-2 shadow-[0_20px_60px_-15px_rgba(62,52,49,0.1)]">
            <div className="relative rounded-[calc(2rem-0.5rem)] border border-[#3e3431]/5 bg-[#fdfcfb] overflow-hidden">
              <ResumeForm
                isLoading={streamingHook.isGenerating}
                tokenBalance={tokenBalance}
                isTokenLoading={false}
                onRefreshTokens={refreshTokenBalance}
                initialFormData={{
                  ...(countryData?.countryCode ? { country: countryData.countryCode } : {}),
                  ...quickStartData,
                }}
                onSubmit={handleSubmit}
              />
            </div>
          </div>
        </motion.div>

        {/* Floating Agents */}
        {mcpAgents.map((agent) => (
          <AgentBadge key={agent.name} {...agent} />
        ))}
        <MobileAgentIcons />
      </div>
    </div>
  );
};

export const AtmosphericHeroSection = () => {
  return (
    <section id="atmospheric-hero" className="relative">
      <TokenProvider>
        <ATSStrengthProvider>
          <AtmosphericHeroContent />
        </ATSStrengthProvider>
      </TokenProvider>
    </section>
  );
};